/**
 *  Header search (theme predictive search, Shto version for Santa Cruz Paleo).
 *  Markup: sections/shto-scp-mnav.liquid (.thb-quick-search under the header). Results: sections/predictive-search.liquid.
 *  - The search icon (#quick-search) opens and closes it; focus moves to the field, and back to the icon on close.
 *  - Closes on Esc, the close button, a click on the dim area below, or when focus leaves it.
 *  - Products, collections, pages and suggestions from /search/suggest; a count is read out through the live region.
 *  - Arrow Down / Up move between the field and the results.
 *  - While open: html.shto-search-open (page behind can't scroll, the Gorgias chat bubble is hidden).
 */
class PredictiveSearch {
  constructor() {
    this.container = document.querySelector('.thb-quick-search');
    this.button = document.getElementById('quick-search');
    if (!this.container || !this.button) return;
    this.section = this.container.closest('.header-section') || document.querySelector('.header-section');
    this.closeButton = this.container.querySelector('.thb-search-close');
    this.input = this.container.querySelector('input[type="search"]');
    this.results = this.container.querySelector('.thb-quick-search--results');
    this.status = this.container.querySelector('[data-shto-search-status]');
    this.cache = new Map();
    this.controller = null;
    this.setupEventListeners();
  }

  isOpen() {
    return !!this.section && this.section.classList.contains('search-open');
  }

  setupEventListeners() {
    const form = this.container.querySelector('form.searchform');
    form.addEventListener('submit', (event) => {
      if (!this.getQuery().length) event.preventDefault();
    });

    this.input.addEventListener('input', debounce(() => this.onChange(), 250));

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.isOpen() ? this.close(true) : this.open();
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.close(true);
      });
    }

    this.container.querySelectorAll('[data-shto-search-close]').forEach((el) => {
      el.addEventListener('click', () => this.close(true));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close(true);
      }
    });

    /* Focus leaving the search (Tab past the last result, or Shift+Tab before the field) closes it, except onto the icon. */
    this.container.addEventListener('focusout', (event) => {
      const to = event.relatedTarget;
      if (!this.isOpen() || !to) return;
      if (this.container.contains(to) || to === this.button) return;
      this.close(false);
    });

    /* Arrow keys: field -> first result, between results, first result -> field. */
    this.container.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = [...this.results.querySelectorAll('a[href]')].filter((a) => a.getClientRects().length);
      if (!links.length) return;
      const i = links.indexOf(document.activeElement);
      if (document.activeElement === this.input) {
        if (event.key === 'ArrowDown') { event.preventDefault(); links[0].focus(); }
        return;
      }
      if (i < 0) return;
      event.preventDefault();
      if (event.key === 'ArrowDown') (links[i + 1] || links[0]).focus();
      else i === 0 ? this.input.focus() : links[i - 1].focus();
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  announce(text) {
    if (!this.status) return;
    this.status.textContent = '';
    window.setTimeout(() => { this.status.textContent = text; }, 60);
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      if (this.controller) this.controller.abort();
      this.results.classList.remove('active', 'loading');
      this.results.innerHTML = '';
      this.announce('');
      return;
    }
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    if (this.cache.has(searchTerm)) {
      this.renderSearchResults(this.cache.get(searchTerm));
      return;
    }

    this.results.classList.add('loading');
    this.results.setAttribute('aria-busy', 'true');

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection,page,query',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
      section_id: 'predictive-search'
    });

    fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const node = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        const markup = node ? node.innerHTML : '';
        if (this.cache.size >= 50) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(searchTerm, markup);
        if (searchTerm === this.getQuery()) this.renderSearchResults(markup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.results.classList.remove('loading', 'active');
        this.results.removeAttribute('aria-busy');
      });
  }

  renderSearchResults(markup) {
    this.results.innerHTML = markup;
    this.results.classList.remove('loading');
    this.results.removeAttribute('aria-busy');
    this.results.classList.add('active');
    const box = this.results.querySelector('[data-summary]');
    this.announce(box ? box.dataset.summary : '');
  }

  open() {
    if (!this.section || this.isOpen()) return;
    document.dispatchEvent(new CustomEvent('search:before-open'));
    this.section.classList.add('search-open');
    document.documentElement.classList.add('shto-search-open');
    this.button.setAttribute('aria-expanded', 'true');
    window.setTimeout(() => {
      this.input.focus({ preventScroll: true });
      const len = this.input.value.length;
      try { this.input.setSelectionRange(len, len); } catch (e) { /* type=search */ }
      if (this.getQuery().length && !this.results.classList.contains('active')) this.getSearchResults(this.getQuery());
    }, 60);
    if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('search:open');
  }

  close(returnFocus) {
    if (!this.section || !this.isOpen()) return;
    this.section.classList.remove('search-open');
    document.documentElement.classList.remove('shto-search-open');
    this.button.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.button.focus({ preventScroll: true });
  }
}
/* Deferred, so app.js (debounce, dispatchCustomEvent) has run: start now rather than after every image and app loads. */
(() => {
  const start = () => {
    if (!window.shtoPredictiveSearch) window.shtoPredictiveSearch = new PredictiveSearch();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
